/**
 * PhotoEditor Component
 * Design editor for marketing materials
 * Available for Ultra members only
 */

import React, { useEffect, useRef, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import {
  ArrowLeft, Image, Download, Save, Loader2, AlertCircle, RefreshCw,
  Type, Square, Circle, Triangle, Trash2, RotateCcw, ZoomIn, ZoomOut,
  AlignLeft, AlignCenter, AlignRight, Bold, Italic, Underline,
  Move, Palette, Layers, Plus, ImagePlus, FileImage, Sparkles, Search,
  Home, Building, Building2, Key, MapPin, Store, Warehouse, Landmark,
  Award, DollarSign, Percent, Calculator, CreditCard, PiggyBank, Wallet, Banknote, Shield, BadgeCheck, Handshake, FileCheck,
  Bed, Bath, Sofa, Car, Wifi, Thermometer, Droplets, TreePine,
  Star, Heart, Flame, Crown, ThumbsUp, Rocket,
  ArrowRight, ArrowLeft as ArrowLeftIcon, ArrowUp, ArrowDown, TrendingUp, TrendingDown,
  MessageCircle, UserPlus, Hash, AtSign, Bookmark, Bell,
  MessageSquare, Quote, Info, AlertCircle as AlertCircleIcon, HelpCircle, Check, Megaphone, Flag,
  X, Diamond, Hexagon, Pentagon, Octagon, Camera, Monitor, Users, Briefcase
} from 'lucide-react';
import { STOCK_PHOTOS, ALL_STOCK_PHOTOS } from '../../../assets/stocks';

// Real Estate focused element categories
const ELEMENT_CATEGORIES = {
  realestate: {
    name: 'Real Estate',
    icon: Home,
    items: [
      { id: 're-home', name: 'Home', icon: Home, color: '#22c55e' },
      { id: 're-building', name: 'Building', icon: Building, color: '#3b82f6' },
      { id: 're-building2', name: 'Commercial', icon: Building2, color: '#8b5cf6' },
      { id: 're-key', name: 'Keys', icon: Key, color: '#eab308' },
      { id: 're-mappin', name: 'Location', icon: MapPin, color: '#ef4444' },
      { id: 're-store', name: 'Store', icon: Store, color: '#f97316' },
      { id: 're-warehouse', name: 'Warehouse', icon: Warehouse, color: '#6b7280' },
      { id: 're-landmark', name: 'Landmark', icon: Landmark, color: '#14b8a6' },
    ]
  },
  badges: {
    name: 'RE Badges',
    icon: Award,
    items: [
      { id: 'badge-sold', name: 'SOLD', text: 'SOLD', bgColor: '#ef4444', textColor: '#ffffff', type: 'badge' },
      { id: 'badge-forsale', name: 'FOR SALE', text: 'FOR SALE', bgColor: '#22c55e', textColor: '#ffffff', type: 'badge' },
      { id: 'badge-pending', name: 'PENDING', text: 'PENDING', bgColor: '#f97316', textColor: '#ffffff', type: 'badge' },
      { id: 'badge-newlisting', name: 'NEW LISTING', text: 'NEW LISTING', bgColor: '#3b82f6', textColor: '#ffffff', type: 'badge' },
      { id: 'badge-openhouse', name: 'OPEN HOUSE', text: 'OPEN HOUSE', bgColor: '#8b5cf6', textColor: '#ffffff', type: 'badge' },
      { id: 'badge-pricecut', name: 'PRICE CUT', text: 'PRICE CUT', bgColor: '#ec4899', textColor: '#ffffff', type: 'badge' },
      { id: 'badge-justlisted', name: 'JUST LISTED', text: 'JUST LISTED', bgColor: '#14b8a6', textColor: '#ffffff', type: 'badge' },
      { id: 'badge-undercont', name: 'UNDER CONTRACT', text: 'UNDER CONTRACT', bgColor: '#eab308', textColor: '#000000', type: 'badge' },
      { id: 'badge-coming', name: 'COMING SOON', text: 'COMING SOON', bgColor: '#6366f1', textColor: '#ffffff', type: 'badge' },
      { id: 'badge-featured', name: 'FEATURED', text: 'FEATURED', bgColor: '#fbbf24', textColor: '#000000', type: 'badge' },
    ]
  },
  financing: {
    name: 'Financing',
    icon: DollarSign,
    items: [
      { id: 'badge-preapproved', name: 'PRE-APPROVED', text: 'PRE-APPROVED', bgColor: '#22c55e', textColor: '#ffffff', type: 'badge' },
      { id: 'badge-lowrate', name: 'LOW RATES', text: 'LOW RATES', bgColor: '#3b82f6', textColor: '#ffffff', type: 'badge' },
      { id: 'badge-nodownpay', name: '0% DOWN', text: '0% DOWN', bgColor: '#8b5cf6', textColor: '#ffffff', type: 'badge' },
      { id: 'badge-fha', name: 'FHA', text: 'FHA LOAN', bgColor: '#0ea5e9', textColor: '#ffffff', type: 'badge' },
      { id: 'badge-va', name: 'VA LOAN', text: 'VA LOAN', bgColor: '#1e40af', textColor: '#ffffff', type: 'badge' },
      { id: 'badge-conv', name: 'CONVENTIONAL', text: 'CONVENTIONAL', bgColor: '#6b7280', textColor: '#ffffff', type: 'badge' },
      { id: 'badge-jumbo', name: 'JUMBO', text: 'JUMBO LOAN', bgColor: '#7c3aed', textColor: '#ffffff', type: 'badge' },
      { id: 'badge-refi', name: 'REFINANCE', text: 'REFINANCE', bgColor: '#14b8a6', textColor: '#ffffff', type: 'badge' },
    ]
  },
  mortgage: {
    name: 'Mortgage',
    icon: Banknote,
    items: [
      { id: 'mort-dollar', name: 'Dollar', icon: DollarSign, color: '#22c55e' },
      { id: 'mort-percent', name: 'Rate', icon: Percent, color: '#3b82f6' },
      { id: 'mort-calc', name: 'Calculator', icon: Calculator, color: '#8b5cf6' },
      { id: 'mort-credit', name: 'Credit Card', icon: CreditCard, color: '#f97316' },
      { id: 'mort-bank', name: 'Bank', icon: Landmark, color: '#6b7280' },
      { id: 'mort-piggy', name: 'Savings', icon: PiggyBank, color: '#ec4899' },
      { id: 'mort-wallet', name: 'Wallet', icon: Wallet, color: '#14b8a6' },
      { id: 'mort-handshake', name: 'Deal', icon: Handshake, color: '#eab308' },
    ]
  },
  amenities: {
    name: 'Amenities',
    icon: Sofa,
    items: [
      { id: 'amen-bed', name: 'Bedroom', icon: Bed, color: '#8b5cf6' },
      { id: 'amen-bath', name: 'Bathroom', icon: Bath, color: '#0ea5e9' },
      { id: 'amen-sofa', name: 'Living Room', icon: Sofa, color: '#f97316' },
      { id: 'amen-car', name: 'Garage', icon: Car, color: '#6b7280' },
      { id: 'amen-wifi', name: 'Smart Home', icon: Wifi, color: '#3b82f6' },
      { id: 'amen-ac', name: 'Climate', icon: Thermometer, color: '#ef4444' },
      { id: 'amen-pool', name: 'Pool', icon: Droplets, color: '#0ea5e9' },
      { id: 'amen-tree', name: 'Garden', icon: TreePine, color: '#22c55e' },
    ]
  },
  popular: {
    name: 'Popular',
    icon: Star,
    items: [
      { id: 'star-gold', name: 'Star', icon: Star, color: '#fbbf24' },
      { id: 'heart-red', name: 'Heart', icon: Heart, color: '#ef4444' },
      { id: 'sparkle', name: 'Sparkle', icon: Sparkles, color: '#a855f7' },
      { id: 'fire', name: 'Fire', icon: Flame, color: '#f97316' },
      { id: 'crown', name: 'Crown', icon: Crown, color: '#eab308' },
      { id: 'trophy', name: 'Trophy', icon: Award, color: '#eab308' },
      { id: 'thumbsup', name: 'Thumbs Up', icon: ThumbsUp, color: '#3b82f6' },
      { id: 'rocket', name: 'Rocket', icon: Rocket, color: '#8b5cf6' },
    ]
  },
  emojis: {
    name: 'Emojis',
    icon: Sparkles,
    items: [
      { id: 'emoji-house', name: 'House', emoji: '🏠', type: 'emoji' },
      { id: 'emoji-keys', name: 'Keys', emoji: '🔑', type: 'emoji' },
      { id: 'emoji-money', name: 'Money', emoji: '💰', type: 'emoji' },
      { id: 'emoji-dollar', name: 'Dollar', emoji: '💵', type: 'emoji' },
      { id: 'emoji-chart', name: 'Chart', emoji: '📈', type: 'emoji' },
      { id: 'emoji-handshake', name: 'Handshake', emoji: '🤝', type: 'emoji' },
      { id: 'emoji-check', name: 'Check', emoji: '✅', type: 'emoji' },
      { id: 'emoji-star', name: 'Star', emoji: '⭐', type: 'emoji' },
      { id: 'emoji-fire', name: 'Fire', emoji: '🔥', type: 'emoji' },
      { id: 'emoji-100', name: '100', emoji: '💯', type: 'emoji' },
      { id: 'emoji-trophy', name: 'Trophy', emoji: '🏆', type: 'emoji' },
      { id: 'emoji-party', name: 'Party', emoji: '🎉', type: 'emoji' },
    ]
  },
};

// Template presets for different design types
const TEMPLATE_PRESETS = {
  business_card: {
    label: 'Business Card',
    width: 1050,
    height: 600,
    thumbnail: 'https://cdn.img.ly/packages/imgly/cesdk-js/latest/assets/templates/cesdk_business_card_1.png'
  },
  instagram_post: {
    label: 'Instagram Post',
    width: 2700,
    height: 3375,
    thumbnail: 'https://cdn.img.ly/packages/imgly/cesdk-js/latest/assets/templates/cesdk_instagram_photo_1.png'
  },
  instagram_story: {
    label: 'Instagram Story',
    width: 1080,
    height: 1920,
    thumbnail: 'https://cdn.img.ly/packages/imgly/cesdk-js/latest/assets/templates/cesdk_instagram_story_1.png'
  },
  facebook_post: {
    label: 'Facebook Post',
    width: 1200,
    height: 630,
    thumbnail: 'https://cdn.img.ly/packages/imgly/cesdk-js/latest/assets/templates/cesdk_postcard_1.png'
  },
  poster: {
    label: 'Poster',
    width: 1080,
    height: 1440,
    thumbnail: 'https://cdn.img.ly/packages/imgly/cesdk-js/latest/assets/templates/cesdk_poster_1.png'
  },
  presentation: {
    label: 'Presentation',
    width: 1920,
    height: 1080,
    thumbnail: 'https://cdn.img.ly/packages/imgly/cesdk-js/latest/assets/templates/cesdk_presentation_1.png'
  },
  flyer: {
    label: 'Flyer',
    width: 850,
    height: 1100,
    thumbnail: 'https://cdn.img.ly/packages/imgly/cesdk-js/latest/assets/templates/cesdk_collage_1.png'
  },
  youtube_thumbnail: {
    label: 'YouTube Thumbnail',
    width: 1280,
    height: 720,
    thumbnail: 'https://cdn.img.ly/packages/imgly/cesdk-js/latest/assets/templates/cesdk_postcard_2.png'
  }
};

// Draw icon function - draws simple vector icons on canvas (outside component for stable reference)
const drawIcon = (ctx, iconName, x, y, size, color) => {
  ctx.save();
  ctx.strokeStyle = color || '#3b82f6';
  ctx.fillStyle = color || '#3b82f6';
  ctx.lineWidth = size / 12;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  const s = size;
  const cx = x + s / 2;
  const cy = y + s / 2;

  switch (iconName) {
    case 'Home':
      ctx.beginPath();
      ctx.moveTo(x + s * 0.1, y + s * 0.45);
      ctx.lineTo(x + s * 0.5, y + s * 0.1);
      ctx.lineTo(x + s * 0.9, y + s * 0.45);
      ctx.lineTo(x + s * 0.9, y + s * 0.9);
      ctx.lineTo(x + s * 0.1, y + s * 0.9);
      ctx.closePath();
      ctx.stroke();
      ctx.beginPath();
      ctx.rect(x + s * 0.4, y + s * 0.55, s * 0.2, s * 0.35);
      ctx.stroke();
      break;
    case 'Building':
    case 'Commercial':
      ctx.strokeRect(x + s * 0.2, y + s * 0.15, s * 0.6, s * 0.75);
      for (let row = 0; row < 3; row++) {
        for (let col = 0; col < 2; col++) {
          ctx.fillRect(x + s * (0.28 + col * 0.25), y + s * (0.25 + row * 0.22), s * 0.15, s * 0.12);
        }
      }
      break;
    case 'Keys':
      ctx.beginPath();
      ctx.arc(x + s * 0.3, y + s * 0.3, s * 0.2, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(x + s * 0.45, y + s * 0.45);
      ctx.lineTo(x + s * 0.85, y + s * 0.85);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(x + s * 0.65, y + s * 0.65);
      ctx.lineTo(x + s * 0.75, y + s * 0.55);
      ctx.moveTo(x + s * 0.75, y + s * 0.75);
      ctx.lineTo(x + s * 0.85, y + s * 0.65);
      ctx.stroke();
      break;
    case 'Location':
      ctx.beginPath();
      ctx.moveTo(cx, y + s * 0.9);
      ctx.quadraticCurveTo(x + s * 0.1, y + s * 0.5, x + s * 0.2, y + s * 0.35);
      ctx.arc(cx, y + s * 0.35, s * 0.3, Math.PI, 0, false);
      ctx.quadraticCurveTo(x + s * 0.9, y + s * 0.5, cx, y + s * 0.9);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(cx, y + s * 0.35, s * 0.1, 0, Math.PI * 2);
      ctx.fill();
      break;
    case 'Store':
      ctx.strokeRect(x + s * 0.15, y + s * 0.3, s * 0.7, s * 0.6);
      ctx.beginPath();
      ctx.moveTo(x + s * 0.1, y + s * 0.3);
      ctx.lineTo(x + s * 0.1, y + s * 0.15);
      ctx.lineTo(x + s * 0.9, y + s * 0.15);
      ctx.lineTo(x + s * 0.9, y + s * 0.3);
      ctx.stroke();
      ctx.strokeRect(x + s * 0.4, y + s * 0.55, s * 0.2, s * 0.35);
      break;
    case 'Warehouse':
      ctx.strokeRect(x + s * 0.1, y + s * 0.25, s * 0.8, s * 0.65);
      ctx.beginPath();
      ctx.moveTo(x + s * 0.1, y + s * 0.25);
      ctx.lineTo(x + s * 0.5, y + s * 0.1);
      ctx.lineTo(x + s * 0.9, y + s * 0.25);
      ctx.stroke();
      ctx.strokeRect(x + s * 0.35, y + s * 0.5, s * 0.3, s * 0.4);
      break;
    case 'Landmark':
      ctx.strokeRect(x + s * 0.2, y + s * 0.35, s * 0.12, s * 0.5);
      ctx.strokeRect(x + s * 0.44, y + s * 0.35, s * 0.12, s * 0.5);
      ctx.strokeRect(x + s * 0.68, y + s * 0.35, s * 0.12, s * 0.5);
      ctx.beginPath();
      ctx.moveTo(x + s * 0.1, y + s * 0.35);
      ctx.lineTo(x + s * 0.5, y + s * 0.1);
      ctx.lineTo(x + s * 0.9, y + s * 0.35);
      ctx.closePath();
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(x + s * 0.1, y + s * 0.85);
      ctx.lineTo(x + s * 0.9, y + s * 0.85);
      ctx.stroke();
      break;
    case 'Dollar':
      ctx.beginPath();
      ctx.moveTo(cx, y + s * 0.1);
      ctx.lineTo(cx, y + s * 0.9);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(cx, y + s * 0.35, s * 0.2, Math.PI * 0.5, Math.PI * 1.5, true);
      ctx.arc(cx, y + s * 0.65, s * 0.2, Math.PI * 1.5, Math.PI * 0.5, true);
      ctx.stroke();
      break;
    case 'Rate':
      ctx.beginPath();
      ctx.arc(x + s * 0.3, y + s * 0.3, s * 0.12, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(x + s * 0.7, y + s * 0.7, s * 0.12, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(x + s * 0.8, y + s * 0.2);
      ctx.lineTo(x + s * 0.2, y + s * 0.8);
      ctx.stroke();
      break;
    case 'Calculator':
      ctx.strokeRect(x + s * 0.2, y + s * 0.1, s * 0.6, s * 0.8);
      ctx.fillRect(x + s * 0.28, y + s * 0.18, s * 0.44, s * 0.15);
      for (let row = 0; row < 3; row++) {
        for (let col = 0; col < 3; col++) {
          ctx.fillRect(x + s * (0.28 + col * 0.15), y + s * (0.42 + row * 0.15), s * 0.1, s * 0.08);
        }
      }
      break;
    case 'Star':
      ctx.beginPath();
      for (let i = 0; i < 5; i++) {
        const angle = (i * 4 * Math.PI) / 5 - Math.PI / 2;
        const px = cx + Math.cos(angle) * s * 0.4;
        const py = cy + Math.sin(angle) * s * 0.4;
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.closePath();
      ctx.stroke();
      break;
    case 'Heart':
      ctx.beginPath();
      ctx.moveTo(cx, y + s * 0.85);
      ctx.bezierCurveTo(x + s * 0.1, y + s * 0.5, x + s * 0.1, y + s * 0.25, cx, y + s * 0.35);
      ctx.bezierCurveTo(x + s * 0.9, y + s * 0.25, x + s * 0.9, y + s * 0.5, cx, y + s * 0.85);
      ctx.stroke();
      break;
    case 'Bedroom':
    case 'Bed':
      ctx.beginPath();
      ctx.moveTo(x + s * 0.1, y + s * 0.9);
      ctx.lineTo(x + s * 0.1, y + s * 0.3);
      ctx.lineTo(x + s * 0.9, y + s * 0.3);
      ctx.lineTo(x + s * 0.9, y + s * 0.9);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(x + s * 0.1, y + s * 0.55);
      ctx.lineTo(x + s * 0.9, y + s * 0.55);
      ctx.stroke();
      ctx.strokeRect(x + s * 0.15, y + s * 0.35, s * 0.25, s * 0.15);
      break;
    case 'Bathroom':
    case 'Bath':
      ctx.beginPath();
      ctx.moveTo(x + s * 0.1, y + s * 0.4);
      ctx.lineTo(x + s * 0.9, y + s * 0.4);
      ctx.quadraticCurveTo(x + s * 0.95, y + s * 0.7, x + s * 0.8, y + s * 0.85);
      ctx.lineTo(x + s * 0.2, y + s * 0.85);
      ctx.quadraticCurveTo(x + s * 0.05, y + s * 0.7, x + s * 0.1, y + s * 0.4);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(x + s * 0.2, y + s * 0.4);
      ctx.lineTo(x + s * 0.2, y + s * 0.2);
      ctx.lineTo(x + s * 0.35, y + s * 0.2);
      ctx.stroke();
      break;
    case 'Living Room':
    case 'Sofa':
      ctx.strokeRect(x + s * 0.1, y + s * 0.25, s * 0.8, s * 0.3);
      ctx.strokeRect(x + s * 0.1, y + s * 0.55, s * 0.8, s * 0.25);
      ctx.strokeRect(x + s * 0.05, y + s * 0.35, s * 0.1, s * 0.45);
      ctx.strokeRect(x + s * 0.85, y + s * 0.35, s * 0.1, s * 0.45);
      break;
    case 'Garage':
    case 'Car':
      ctx.beginPath();
      ctx.moveTo(x + s * 0.1, y + s * 0.6);
      ctx.lineTo(x + s * 0.25, y + s * 0.35);
      ctx.lineTo(x + s * 0.75, y + s * 0.35);
      ctx.lineTo(x + s * 0.9, y + s * 0.6);
      ctx.lineTo(x + s * 0.1, y + s * 0.6);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(x + s * 0.3, y + s * 0.7, s * 0.1, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(x + s * 0.7, y + s * 0.7, s * 0.1, 0, Math.PI * 2);
      ctx.stroke();
      break;
    case 'Smart Home':
    case 'Wifi':
      ctx.beginPath();
      ctx.arc(cx, y + s * 0.8, s * 0.15, Math.PI, 0, false);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(cx, y + s * 0.8, s * 0.3, Math.PI, 0, false);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(cx, y + s * 0.8, s * 0.45, Math.PI, 0, false);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(cx, y + s * 0.8, s * 0.05, 0, Math.PI * 2);
      ctx.fill();
      break;
    case 'Climate':
    case 'Thermometer':
      ctx.beginPath();
      ctx.arc(cx, y + s * 0.75, s * 0.15, 0, Math.PI * 2);
      ctx.stroke();
      ctx.strokeRect(x + s * 0.42, y + s * 0.15, s * 0.16, s * 0.5);
      ctx.beginPath();
      ctx.arc(cx, y + s * 0.75, s * 0.08, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillRect(x + s * 0.46, y + s * 0.4, s * 0.08, s * 0.28);
      break;
    case 'Pool':
    case 'Droplets':
      ctx.beginPath();
      ctx.moveTo(cx, y + s * 0.15);
      ctx.quadraticCurveTo(x + s * 0.8, y + s * 0.5, x + s * 0.65, y + s * 0.75);
      ctx.arc(cx, y + s * 0.75, s * 0.15, 0, Math.PI, false);
      ctx.quadraticCurveTo(x + s * 0.2, y + s * 0.5, cx, y + s * 0.15);
      ctx.stroke();
      break;
    case 'Garden':
    case 'TreePine':
      ctx.beginPath();
      ctx.moveTo(cx, y + s * 0.1);
      ctx.lineTo(x + s * 0.75, y + s * 0.7);
      ctx.lineTo(x + s * 0.25, y + s * 0.7);
      ctx.closePath();
      ctx.stroke();
      ctx.fillRect(x + s * 0.42, y + s * 0.7, s * 0.16, s * 0.2);
      break;
    case 'Sparkle':
    case 'Sparkles':
      ctx.beginPath();
      ctx.moveTo(cx, y + s * 0.1);
      ctx.lineTo(cx + s * 0.08, cy);
      ctx.lineTo(cx + s * 0.4, cy);
      ctx.lineTo(cx + s * 0.12, cy + s * 0.12);
      ctx.lineTo(cx + s * 0.2, y + s * 0.9);
      ctx.lineTo(cx, cy + s * 0.2);
      ctx.lineTo(cx - s * 0.2, y + s * 0.9);
      ctx.lineTo(cx - s * 0.12, cy + s * 0.12);
      ctx.lineTo(cx - s * 0.4, cy);
      ctx.lineTo(cx - s * 0.08, cy);
      ctx.closePath();
      ctx.stroke();
      break;
    case 'Fire':
    case 'Flame':
      ctx.beginPath();
      ctx.moveTo(cx, y + s * 0.9);
      ctx.quadraticCurveTo(x + s * 0.2, y + s * 0.6, x + s * 0.3, y + s * 0.3);
      ctx.quadraticCurveTo(x + s * 0.4, y + s * 0.5, cx, y + s * 0.1);
      ctx.quadraticCurveTo(x + s * 0.6, y + s * 0.5, x + s * 0.7, y + s * 0.3);
      ctx.quadraticCurveTo(x + s * 0.8, y + s * 0.6, cx, y + s * 0.9);
      ctx.stroke();
      break;
    case 'Crown':
      ctx.beginPath();
      ctx.moveTo(x + s * 0.1, y + s * 0.8);
      ctx.lineTo(x + s * 0.1, y + s * 0.4);
      ctx.lineTo(x + s * 0.3, y + s * 0.55);
      ctx.lineTo(cx, y + s * 0.2);
      ctx.lineTo(x + s * 0.7, y + s * 0.55);
      ctx.lineTo(x + s * 0.9, y + s * 0.4);
      ctx.lineTo(x + s * 0.9, y + s * 0.8);
      ctx.closePath();
      ctx.stroke();
      break;
    case 'Trophy':
    case 'Award':
      ctx.beginPath();
      ctx.moveTo(x + s * 0.25, y + s * 0.2);
      ctx.lineTo(x + s * 0.75, y + s * 0.2);
      ctx.quadraticCurveTo(x + s * 0.7, y + s * 0.55, cx, y + s * 0.6);
      ctx.quadraticCurveTo(x + s * 0.3, y + s * 0.55, x + s * 0.25, y + s * 0.2);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(x + s * 0.2, y + s * 0.35, s * 0.08, Math.PI * 0.5, Math.PI * 1.5, false);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(x + s * 0.8, y + s * 0.35, s * 0.08, Math.PI * 1.5, Math.PI * 0.5, false);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(cx, y + s * 0.6);
      ctx.lineTo(cx, y + s * 0.75);
      ctx.stroke();
      ctx.strokeRect(x + s * 0.35, y + s * 0.75, s * 0.3, s * 0.1);
      break;
    case 'Thumbs Up':
    case 'ThumbsUp':
      ctx.beginPath();
      ctx.moveTo(x + s * 0.3, y + s * 0.45);
      ctx.lineTo(x + s * 0.3, y + s * 0.2);
      ctx.quadraticCurveTo(x + s * 0.5, y + s * 0.1, x + s * 0.6, y + s * 0.3);
      ctx.lineTo(x + s * 0.85, y + s * 0.3);
      ctx.lineTo(x + s * 0.85, y + s * 0.85);
      ctx.lineTo(x + s * 0.4, y + s * 0.85);
      ctx.lineTo(x + s * 0.3, y + s * 0.45);
      ctx.stroke();
      ctx.strokeRect(x + s * 0.1, y + s * 0.45, s * 0.2, s * 0.4);
      break;
    case 'Rocket':
      ctx.beginPath();
      ctx.moveTo(cx, y + s * 0.1);
      ctx.quadraticCurveTo(x + s * 0.7, y + s * 0.3, x + s * 0.65, y + s * 0.7);
      ctx.lineTo(x + s * 0.35, y + s * 0.7);
      ctx.quadraticCurveTo(x + s * 0.3, y + s * 0.3, cx, y + s * 0.1);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(x + s * 0.35, y + s * 0.6);
      ctx.lineTo(x + s * 0.2, y + s * 0.85);
      ctx.lineTo(x + s * 0.35, y + s * 0.7);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(x + s * 0.65, y + s * 0.6);
      ctx.lineTo(x + s * 0.8, y + s * 0.85);
      ctx.lineTo(x + s * 0.65, y + s * 0.7);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(cx, y + s * 0.4, s * 0.08, 0, Math.PI * 2);
      ctx.stroke();
      break;
    case 'Credit Card':
      ctx.strokeRect(x + s * 0.1, y + s * 0.25, s * 0.8, s * 0.5);
      ctx.fillRect(x + s * 0.1, y + s * 0.38, s * 0.8, s * 0.12);
      break;
    case 'Savings':
      ctx.beginPath();
      ctx.ellipse(cx, cy, s * 0.35, s * 0.25, 0, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.ellipse(x + s * 0.35, y + s * 0.32, s * 0.08, s * 0.1, -0.3, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.ellipse(x + s * 0.78, cy, s * 0.08, s * 0.06, 0, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(x + s * 0.35, y + s * 0.7);
      ctx.lineTo(x + s * 0.35, y + s * 0.85);
      ctx.moveTo(x + s * 0.65, y + s * 0.7);
      ctx.lineTo(x + s * 0.65, y + s * 0.85);
      ctx.stroke();
      break;
    case 'Wallet':
      ctx.strokeRect(x + s * 0.15, y + s * 0.25, s * 0.7, s * 0.5);
      ctx.beginPath();
      ctx.moveTo(x + s * 0.15, y + s * 0.25);
      ctx.lineTo(x + s * 0.15, y + s * 0.15);
      ctx.lineTo(x + s * 0.85, y + s * 0.15);
      ctx.lineTo(x + s * 0.85, y + s * 0.25);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(x + s * 0.55, y + s * 0.45);
      ctx.lineTo(x + s * 0.8, y + s * 0.45);
      ctx.lineTo(x + s * 0.8, y + s * 0.65);
      ctx.stroke();
      break;
    case 'Deal':
    case 'Handshake':
      ctx.beginPath();
      ctx.moveTo(x + s * 0.1, y + s * 0.5);
      ctx.lineTo(x + s * 0.35, y + s * 0.5);
      ctx.lineTo(x + s * 0.5, y + s * 0.4);
      ctx.lineTo(x + s * 0.65, y + s * 0.5);
      ctx.lineTo(x + s * 0.9, y + s * 0.5);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(x + s * 0.35, y + s * 0.5);
      ctx.lineTo(x + s * 0.35, y + s * 0.65);
      ctx.moveTo(x + s * 0.65, y + s * 0.5);
      ctx.lineTo(x + s * 0.65, y + s * 0.65);
      ctx.stroke();
      break;
    default:
      ctx.beginPath();
      ctx.arc(cx, cy, s * 0.4, 0, Math.PI * 2);
      ctx.stroke();
      ctx.font = `bold ${s * 0.4}px Arial`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(iconName.charAt(0), cx, cy);
      break;
  }
  ctx.restore();
};

// Built-in canvas editor component
const CanvasEditor = React.forwardRef(({ template, initialElements, initialBackgroundColor, onChanges }, ref) => {
  const canvasRef = useRef(null);
  const [elements, setElements] = useState([]);
  const [selectedElement, setSelectedElement] = useState(null);
  const [tool, setTool] = useState('select');
  const [zoom, setZoom] = useState(0.5);
  const [backgroundColor, setBackgroundColor] = useState(initialBackgroundColor || '#ffffff');
  const [textColor, setTextColor] = useState('#000000');
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0, elX: 0, elY: 0 });
  const [showElements, setShowElements] = useState(false);
  const [elementCategory, setElementCategory] = useState('realestate');
  const [elementSearch, setElementSearch] = useState('');
  const [showStockPhotos, setShowStockPhotos] = useState(false);
  const [stockCategory, setStockCategory] = useState('all');
  const [stockSearch, setStockSearch] = useState('');
  const [isLoadingImage, setIsLoadingImage] = useState(false);
  const fileInputRef = useRef(null);

  const canvasWidth = template.width;
  const canvasHeight = template.height;
  const HANDLE_SIZE = 10;

  // Load initial elements from project
  useEffect(() => {
    if (initialElements && initialElements.length > 0) {
      // Restore images from imageSrc
      const loadElements = async () => {
        const loadedElements = await Promise.all(
          initialElements.map(async (el) => {
            if (el.type === 'image' && el.imageSrc) {
              return new Promise((resolve) => {
                const img = new window.Image();
                img.onload = () => resolve({ ...el, image: img });
                img.onerror = () => resolve(el); // Skip failed images
                img.src = el.imageSrc;
              });
            }
            return el;
          })
        );
        setElements(loadedElements);
      };
      loadElements();
    }
  }, [initialElements]);

  // Generate thumbnail for saving
  const generateThumbnail = useCallback(() => {
    return new Promise((resolve) => {
      const canvas = canvasRef.current;
      if (!canvas) {
        resolve(null);
        return;
      }

      // Create thumbnail canvas (max 400px)
      const thumbCanvas = document.createElement('canvas');
      const maxSize = 400;
      const scale = Math.min(maxSize / canvasWidth, maxSize / canvasHeight);
      thumbCanvas.width = canvasWidth * scale;
      thumbCanvas.height = canvasHeight * scale;

      const ctx = thumbCanvas.getContext('2d');
      ctx.scale(scale, scale);

      // Draw background
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);

      // Draw elements (simplified - just draw what's on main canvas)
      elements.forEach(el => {
        ctx.save();
        if (el.type === 'text') {
          ctx.font = `${el.fontStyle || ''} ${el.fontWeight || ''} ${el.fontSize || 32}px ${el.fontFamily || 'Arial'}`.trim();
          ctx.fillStyle = el.color || '#000000';
          ctx.textAlign = el.align || 'left';
          ctx.textBaseline = 'top';
          ctx.fillText(el.text, el.x, el.y);
        } else if (el.type === 'rectangle') {
          ctx.fillStyle = el.color || '#3b82f6';
          ctx.fillRect(el.x, el.y, el.width, el.height);
        } else if (el.type === 'circle') {
          ctx.fillStyle = el.color || '#3b82f6';
          ctx.beginPath();
          ctx.arc(el.x + el.width / 2, el.y + el.height / 2, Math.min(el.width, el.height) / 2, 0, Math.PI * 2);
          ctx.fill();
        } else if (el.type === 'image' && el.image) {
          ctx.drawImage(el.image, el.x, el.y, el.width, el.height);
        } else if (el.type === 'badge') {
          ctx.fillStyle = el.bgColor || '#3b82f6';
          ctx.beginPath();
          ctx.roundRect(el.x, el.y, el.width, el.height, 10);
          ctx.fill();
          ctx.font = `bold ${el.fontSize || 24}px Arial`;
          ctx.fillStyle = el.textColor || '#ffffff';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(el.text || '', el.x + el.width / 2, el.y + el.height / 2);
        } else if (el.type === 'icon') {
          const size = Math.min(el.width, el.height);
          drawIcon(ctx, el.iconName, el.x, el.y, size, el.color);
        }
        ctx.restore();
      });

      resolve(thumbCanvas.toDataURL('image/png', 0.8));
    });
  }, [canvasWidth, canvasHeight, backgroundColor, elements]);

  // Expose methods via ref
  React.useImperativeHandle(ref, () => ({
    elements,
    backgroundColor,
    generateThumbnail
  }), [elements, backgroundColor, generateThumbnail]);

  // Notify parent of changes
  const notifyChanges = useCallback(() => {
    if (onChanges) onChanges();
  }, [onChanges]);

  // Wrap setElements to track changes
  const updateElements = useCallback((updater) => {
    setElements(updater);
    notifyChanges();
  }, [notifyChanges]);

  // Wrap setBackgroundColor to track changes
  const updateBackgroundColor = useCallback((color) => {
    setBackgroundColor(color);
    notifyChanges();
  }, [notifyChanges]);

  // Draw canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const displayWidth = canvasWidth * zoom;
    const displayHeight = canvasHeight * zoom;

    canvas.width = canvasWidth;
    canvas.height = canvasHeight;
    canvas.style.width = `${displayWidth}px`;
    canvas.style.height = `${displayHeight}px`;

    // Clear and fill background
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    // Draw elements
    elements.forEach((el, index) => {
      ctx.save();

      if (el.type === 'text') {
        ctx.font = `${el.fontStyle || ''} ${el.fontWeight || ''} ${el.fontSize || 32}px ${el.fontFamily || 'Arial'}`.trim();
        ctx.fillStyle = el.color || '#000000';
        ctx.textAlign = el.align || 'left';
        ctx.textBaseline = 'top';
        ctx.fillText(el.text, el.x, el.y);
      } else if (el.type === 'rectangle') {
        ctx.fillStyle = el.color || '#3b82f6';
        ctx.fillRect(el.x, el.y, el.width, el.height);
      } else if (el.type === 'circle') {
        ctx.fillStyle = el.color || '#3b82f6';
        ctx.beginPath();
        ctx.arc(el.x + el.width / 2, el.y + el.height / 2, Math.min(el.width, el.height) / 2, 0, Math.PI * 2);
        ctx.fill();
      } else if (el.type === 'triangle') {
        ctx.fillStyle = el.color || '#3b82f6';
        ctx.beginPath();
        ctx.moveTo(el.x + el.width / 2, el.y);
        ctx.lineTo(el.x + el.width, el.y + el.height);
        ctx.lineTo(el.x, el.y + el.height);
        ctx.closePath();
        ctx.fill();
      } else if (el.type === 'image' && el.image) {
        ctx.drawImage(el.image, el.x, el.y, el.width, el.height);
      } else if (el.type === 'badge' || el.type === 'sticker') {
        // Draw badge/sticker background with rounded corners
        const radius = 10;
        ctx.fillStyle = el.bgColor || el.color || '#3b82f6';
        ctx.beginPath();
        ctx.roundRect(el.x, el.y, el.width, el.height, radius);
        ctx.fill();
        // Draw badge text
        const text = el.text || el.name?.toUpperCase() || '';
        ctx.font = `${el.fontWeight || 'bold'} ${el.fontSize || 24}px Arial`;
        ctx.fillStyle = el.textColor || '#ffffff';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(text, el.x + el.width / 2, el.y + el.height / 2);
      } else if (el.type === 'icon') {
        // Draw icon using canvas drawing functions
        const size = Math.min(el.width, el.height);
        drawIcon(ctx, el.iconName, el.x, el.y, size, el.color);
      }

      // Draw selection and resize handles
      if (selectedElement === index) {
        ctx.strokeStyle = '#3b82f6';
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 5]);

        let selX, selY, selW, selH;
        if (el.type === 'text') {
          const metrics = ctx.measureText(el.text);
          selX = el.x - 5;
          selY = el.y - 5;
          selW = metrics.width + 10;
          selH = (el.fontSize || 32) + 10;
        } else {
          selX = el.x - 5;
          selY = el.y - 5;
          selW = el.width + 10;
          selH = el.height + 10;
        }

        ctx.strokeRect(selX, selY, selW, selH);

        // Draw resize handles for non-text elements
        if (el.type !== 'text') {
          ctx.setLineDash([]);
          ctx.fillStyle = '#ffffff';
          ctx.strokeStyle = '#3b82f6';
          ctx.lineWidth = 2;

          const handles = [
            { x: el.x - HANDLE_SIZE/2, y: el.y - HANDLE_SIZE/2 }, // nw
            { x: el.x + el.width/2 - HANDLE_SIZE/2, y: el.y - HANDLE_SIZE/2 }, // n
            { x: el.x + el.width - HANDLE_SIZE/2, y: el.y - HANDLE_SIZE/2 }, // ne
            { x: el.x + el.width - HANDLE_SIZE/2, y: el.y + el.height/2 - HANDLE_SIZE/2 }, // e
            { x: el.x + el.width - HANDLE_SIZE/2, y: el.y + el.height - HANDLE_SIZE/2 }, // se
            { x: el.x + el.width/2 - HANDLE_SIZE/2, y: el.y + el.height - HANDLE_SIZE/2 }, // s
            { x: el.x - HANDLE_SIZE/2, y: el.y + el.height - HANDLE_SIZE/2 }, // sw
            { x: el.x - HANDLE_SIZE/2, y: el.y + el.height/2 - HANDLE_SIZE/2 }, // w
          ];

          handles.forEach(h => {
            ctx.fillRect(h.x, h.y, HANDLE_SIZE, HANDLE_SIZE);
            ctx.strokeRect(h.x, h.y, HANDLE_SIZE, HANDLE_SIZE);
          });
        }
      }

      ctx.restore();
    });
  }, [elements, selectedElement, zoom, backgroundColor, canvasWidth, canvasHeight, HANDLE_SIZE]);

  // Add text element
  const addText = useCallback(() => {
    setElements(prev => [...prev, {
      type: 'text',
      text: 'Double-click to edit',
      x: 100,
      y: 100,
      fontSize: 32,
      fontFamily: 'Arial',
      fontWeight: 'normal',
      fontStyle: 'normal',
      color: textColor,
      align: 'left'
    }]);
    setSelectedElement(elements.length);
  }, [elements.length, textColor]);

  // Add shape element
  const addShape = useCallback((shapeType) => {
    setElements(prev => [...prev, {
      type: shapeType,
      x: 100,
      y: 100,
      width: 150,
      height: shapeType === 'circle' ? 150 : 100,
      color: '#3b82f6'
    }]);
    setSelectedElement(elements.length);
  }, [elements.length]);

  // Add element from Elements panel (badges, emojis, icons)
  const addElement = useCallback((item) => {
    if (item.type === 'badge') {
      // Add badge as styled rectangle with text
      const textLen = item.text.length;
      setElements(prev => [...prev, {
        type: 'badge',
        text: item.text,
        x: 150,
        y: 150,
        width: Math.max(180, textLen * 20 + 60),
        height: 60,
        bgColor: item.bgColor,
        textColor: item.textColor,
        fontSize: 24,
        fontWeight: 'bold'
      }]);
    } else if (item.type === 'emoji') {
      // Add emoji as text element
      setElements(prev => [...prev, {
        type: 'text',
        text: item.emoji,
        x: 150,
        y: 150,
        fontSize: 80,
        fontFamily: 'Arial',
        color: '#000000'
      }]);
    } else if (item.icon) {
      // Add icon as canvas-drawn element
      setElements(prev => [...prev, {
        type: 'icon',
        iconName: item.name,
        x: 150,
        y: 150,
        width: 100,
        height: 100,
        color: item.color || '#3b82f6',
      }]);
    }
    setSelectedElement(elements.length);
    setShowElements(false);
  }, [elements.length]);

  // Add stock photo to canvas
  const addStockPhoto = useCallback((photo) => {
    setIsLoadingImage(true);
    const img = new window.Image();

    img.onload = () => {
      // Keep original size, center on canvas
      const width = img.naturalWidth;
      const height = img.naturalHeight;

      setElements(prev => [...prev, {
        type: 'image',
        image: img,
        stockPhotoId: photo.id,
        x: (canvasWidth - width) / 2,
        y: (canvasHeight - height) / 2,
        width,
        height
      }]);
      setSelectedElement(elements.length);
      setShowStockPhotos(false);
      setIsLoadingImage(false);
    };

    img.onerror = () => {
      setIsLoadingImage(false);
      alert('Failed to load stock photo. Please try another.');
    };

    img.src = photo.src;
  }, [canvasWidth, canvasHeight, elements.length]);

  // Add image
  const handleImageUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsLoadingImage(true);
    const img = new window.Image();

    img.onload = () => {
      // Keep original size, center on canvas
      const width = img.naturalWidth;
      const height = img.naturalHeight;

      setElements(prev => [...prev, {
        type: 'image',
        image: img,
        x: (canvasWidth - width) / 2,
        y: (canvasHeight - height) / 2,
        width,
        height
      }]);
      setSelectedElement(elements.length);
      setIsLoadingImage(false);
      // Reset file input so same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    };

    img.onerror = () => {
      setIsLoadingImage(false);
      alert('Failed to load image. Please try another file.');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    };

    img.src = URL.createObjectURL(file);
  }, [canvasWidth, canvasHeight, elements.length]);

  // Handle canvas click
  const handleCanvasClick = useCallback((e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left) / zoom;
    const y = (e.clientY - rect.top) / zoom;

    // Find clicked element
    let found = -1;
    for (let i = elements.length - 1; i >= 0; i--) {
      const el = elements[i];
      if (el.type === 'text') {
        const canvas2 = document.createElement('canvas');
        const ctx = canvas2.getContext('2d');
        ctx.font = `${el.fontSize || 32}px ${el.fontFamily || 'Arial'}`;
        const metrics = ctx.measureText(el.text);
        if (x >= el.x && x <= el.x + metrics.width && y >= el.y && y <= el.y + (el.fontSize || 32)) {
          found = i;
          break;
        }
      } else {
        if (x >= el.x && x <= el.x + el.width && y >= el.y && y <= el.y + el.height) {
          found = i;
          break;
        }
      }
    }
    setSelectedElement(found >= 0 ? found : null);
  }, [elements, zoom]);

  // Check if click is on a resize handle
  const getResizeHandle = useCallback((x, y, el) => {
    if (!el || el.type === 'text') return null;

    const handles = [
      { name: 'nw', x: el.x - HANDLE_SIZE/2, y: el.y - HANDLE_SIZE/2 },
      { name: 'n', x: el.x + el.width/2 - HANDLE_SIZE/2, y: el.y - HANDLE_SIZE/2 },
      { name: 'ne', x: el.x + el.width - HANDLE_SIZE/2, y: el.y - HANDLE_SIZE/2 },
      { name: 'e', x: el.x + el.width - HANDLE_SIZE/2, y: el.y + el.height/2 - HANDLE_SIZE/2 },
      { name: 'se', x: el.x + el.width - HANDLE_SIZE/2, y: el.y + el.height - HANDLE_SIZE/2 },
      { name: 's', x: el.x + el.width/2 - HANDLE_SIZE/2, y: el.y + el.height - HANDLE_SIZE/2 },
      { name: 'sw', x: el.x - HANDLE_SIZE/2, y: el.y + el.height - HANDLE_SIZE/2 },
      { name: 'w', x: el.x - HANDLE_SIZE/2, y: el.y + el.height/2 - HANDLE_SIZE/2 },
    ];

    for (const h of handles) {
      if (x >= h.x && x <= h.x + HANDLE_SIZE && y >= h.y && y <= h.y + HANDLE_SIZE) {
        return h.name;
      }
    }
    return null;
  }, [HANDLE_SIZE]);

  // Handle mouse down for dragging or resizing
  const handleMouseDown = useCallback((e) => {
    if (selectedElement === null) return;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left) / zoom;
    const y = (e.clientY - rect.top) / zoom;

    const el = elements[selectedElement];

    // Check if clicking on a resize handle
    const handle = getResizeHandle(x, y, el);
    if (handle) {
      setIsResizing(true);
      setResizeHandle(handle);
      setResizeStart({
        x,
        y,
        width: el.width,
        height: el.height,
        elX: el.x,
        elY: el.y
      });
      return;
    }

    setIsDragging(true);
    setDragOffset({ x: x - el.x, y: y - el.y });
  }, [selectedElement, elements, zoom, getResizeHandle]);

  // Handle mouse move for dragging or resizing
  const handleMouseMove = useCallback((e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left) / zoom;
    const y = (e.clientY - rect.top) / zoom;

    // Update cursor based on hover
    if (selectedElement !== null && !isDragging && !isResizing) {
      const el = elements[selectedElement];
      const handle = getResizeHandle(x, y, el);
      if (handle) {
        const cursors = {
          'nw': 'nwse-resize', 'se': 'nwse-resize',
          'ne': 'nesw-resize', 'sw': 'nesw-resize',
          'n': 'ns-resize', 's': 'ns-resize',
          'e': 'ew-resize', 'w': 'ew-resize'
        };
        canvas.style.cursor = cursors[handle] || 'move';
      } else {
        canvas.style.cursor = 'move';
      }
    } else if (!isDragging && !isResizing) {
      canvas.style.cursor = 'crosshair';
    }

    // Handle resizing
    if (isResizing && selectedElement !== null && resizeHandle) {
      const deltaX = x - resizeStart.x;
      const deltaY = y - resizeStart.y;
      const shiftKey = e.shiftKey;
      const aspectRatio = resizeStart.width / resizeStart.height;

      setElements(prev => prev.map((el, i) => {
        if (i !== selectedElement) return el;

        let newX = resizeStart.elX;
        let newY = resizeStart.elY;
        let newWidth = resizeStart.width;
        let newHeight = resizeStart.height;

        // Calculate new dimensions based on handle
        switch (resizeHandle) {
          case 'se':
            newWidth = Math.max(20, resizeStart.width + deltaX);
            newHeight = Math.max(20, resizeStart.height + deltaY);
            if (shiftKey) {
              newHeight = newWidth / aspectRatio;
            }
            break;
          case 'sw':
            newWidth = Math.max(20, resizeStart.width - deltaX);
            newHeight = Math.max(20, resizeStart.height + deltaY);
            if (shiftKey) {
              newHeight = newWidth / aspectRatio;
            }
            newX = resizeStart.elX + (resizeStart.width - newWidth);
            break;
          case 'ne':
            newWidth = Math.max(20, resizeStart.width + deltaX);
            newHeight = Math.max(20, resizeStart.height - deltaY);
            if (shiftKey) {
              newHeight = newWidth / aspectRatio;
            }
            newY = resizeStart.elY + (resizeStart.height - newHeight);
            break;
          case 'nw':
            newWidth = Math.max(20, resizeStart.width - deltaX);
            newHeight = Math.max(20, resizeStart.height - deltaY);
            if (shiftKey) {
              newHeight = newWidth / aspectRatio;
            }
            newX = resizeStart.elX + (resizeStart.width - newWidth);
            newY = resizeStart.elY + (resizeStart.height - newHeight);
            break;
          case 'n':
            newHeight = Math.max(20, resizeStart.height - deltaY);
            newY = resizeStart.elY + (resizeStart.height - newHeight);
            if (shiftKey) {
              newWidth = newHeight * aspectRatio;
            }
            break;
          case 's':
            newHeight = Math.max(20, resizeStart.height + deltaY);
            if (shiftKey) {
              newWidth = newHeight * aspectRatio;
            }
            break;
          case 'e':
            newWidth = Math.max(20, resizeStart.width + deltaX);
            if (shiftKey) {
              newHeight = newWidth / aspectRatio;
            }
            break;
          case 'w':
            newWidth = Math.max(20, resizeStart.width - deltaX);
            newX = resizeStart.elX + (resizeStart.width - newWidth);
            if (shiftKey) {
              newHeight = newWidth / aspectRatio;
            }
            break;
          default:
            break;
        }

        return { ...el, x: newX, y: newY, width: newWidth, height: newHeight };
      }));
      return;
    }

    // Handle dragging
    if (isDragging && selectedElement !== null) {
      setElements(prev => prev.map((el, i) =>
        i === selectedElement ? { ...el, x: x - dragOffset.x, y: y - dragOffset.y } : el
      ));
    }
  }, [isDragging, isResizing, selectedElement, zoom, dragOffset, resizeHandle, resizeStart, elements, getResizeHandle]);

  // Handle mouse up
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setIsResizing(false);
    setResizeHandle(null);
  }, []);

  // Handle double click to edit text
  const handleDoubleClick = useCallback((e) => {
    if (selectedElement === null) return;
    const el = elements[selectedElement];
    if (el.type !== 'text') return;

    const newText = prompt('Edit text:', el.text);
    if (newText !== null) {
      setElements(prev => prev.map((el, i) =>
        i === selectedElement ? { ...el, text: newText } : el
      ));
    }
  }, [selectedElement, elements]);

  // Delete selected element
  const deleteSelected = useCallback(() => {
    if (selectedElement !== null) {
      setElements(prev => prev.filter((_, i) => i !== selectedElement));
      setSelectedElement(null);
    }
  }, [selectedElement]);

  // Update selected element color
  const updateSelectedColor = useCallback((color) => {
    if (selectedElement !== null) {
      setElements(prev => prev.map((el, i) =>
        i === selectedElement ? { ...el, color } : el
      ));
    }
  }, [selectedElement]);

  // Export canvas
  const handleExport = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Create a clean export without selection
    const exportCanvas = document.createElement('canvas');
    exportCanvas.width = canvasWidth;
    exportCanvas.height = canvasHeight;
    const ctx = exportCanvas.getContext('2d');

    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    elements.forEach(el => {
      ctx.save();
      if (el.type === 'text') {
        ctx.font = `${el.fontStyle || ''} ${el.fontWeight || ''} ${el.fontSize || 32}px ${el.fontFamily || 'Arial'}`.trim();
        ctx.fillStyle = el.color || '#000000';
        ctx.textAlign = el.align || 'left';
        ctx.textBaseline = 'top';
        ctx.fillText(el.text, el.x, el.y);
      } else if (el.type === 'rectangle') {
        ctx.fillStyle = el.color || '#3b82f6';
        ctx.fillRect(el.x, el.y, el.width, el.height);
      } else if (el.type === 'circle') {
        ctx.fillStyle = el.color || '#3b82f6';
        ctx.beginPath();
        ctx.arc(el.x + el.width / 2, el.y + el.height / 2, Math.min(el.width, el.height) / 2, 0, Math.PI * 2);
        ctx.fill();
      } else if (el.type === 'triangle') {
        ctx.fillStyle = el.color || '#3b82f6';
        ctx.beginPath();
        ctx.moveTo(el.x + el.width / 2, el.y);
        ctx.lineTo(el.x + el.width, el.y + el.height);
        ctx.lineTo(el.x, el.y + el.height);
        ctx.closePath();
        ctx.fill();
      } else if (el.type === 'image' && el.image) {
        ctx.drawImage(el.image, el.x, el.y, el.width, el.height);
      } else if (el.type === 'badge' || el.type === 'sticker') {
        // Draw badge/sticker background with rounded corners
        const radius = 10;
        ctx.fillStyle = el.bgColor || el.color || '#3b82f6';
        ctx.beginPath();
        ctx.roundRect(el.x, el.y, el.width, el.height, radius);
        ctx.fill();
        // Draw badge text
        const text = el.text || el.name?.toUpperCase() || '';
        ctx.font = `${el.fontWeight || 'bold'} ${el.fontSize || 24}px Arial`;
        ctx.fillStyle = el.textColor || '#ffffff';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(text, el.x + el.width / 2, el.y + el.height / 2);
      } else if (el.type === 'icon') {
        // Draw icon using canvas drawing functions
        const size = Math.min(el.width, el.height);
        drawIcon(ctx, el.iconName, el.x, el.y, size, el.color);
      }
      ctx.restore();
    });

    exportCanvas.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `design-${Date.now()}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 'image/png');
  }, [elements, backgroundColor, canvasWidth, canvasHeight]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedElement !== null && document.activeElement.tagName !== 'INPUT') {
          deleteSelected();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedElement, deleteSelected]);

  return (
    <div className="flex h-full bg-[#f5f2ec] dark:bg-slate-950">
      {/* Left Toolbar */}
      <div className="w-16 bg-white/80 dark:bg-slate-900 border-r border-white/60 dark:border-slate-800 flex flex-col items-center py-4 gap-2 backdrop-blur shadow-sm">
        <span className="text-[9px] uppercase tracking-[0.2em] text-slate-400">Tools</span>
        <button
          onClick={() => setTool('select')}
          className={`p-3 rounded-xl transition-colors ${tool === 'select' ? 'bg-teal-100 dark:bg-teal-900/50 text-teal-700' : 'hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-400'}`}
          title="Select"
        >
          <Move className="w-5 h-5" />
        </button>
        <button
          onClick={addText}
          className="p-3 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-400 transition-colors"
          title="Add Text"
        >
          <Type className="w-5 h-5" />
        </button>
        <button
          onClick={() => addShape('rectangle')}
          className="p-3 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-400 transition-colors"
          title="Add Rectangle"
        >
          <Square className="w-5 h-5" />
        </button>
        <button
          onClick={() => addShape('circle')}
          className="p-3 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-400 transition-colors"
          title="Add Circle"
        >
          <Circle className="w-5 h-5" />
        </button>
        <button
          onClick={() => addShape('triangle')}
          className="p-3 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-400 transition-colors"
          title="Add Triangle"
        >
          <Triangle className="w-5 h-5" />
        </button>
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={isLoadingImage}
          className={`p-3 rounded-xl transition-colors ${isLoadingImage ? 'bg-teal-100 dark:bg-teal-900/50 text-teal-700' : 'hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-400'}`}
          title={isLoadingImage ? "Loading image..." : "Add Image"}
        >
          {isLoadingImage ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImagePlus className="w-5 h-5" />}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          className="hidden"
        />

        <div className="h-px w-8 bg-slate-300 dark:bg-slate-600 my-2" />

        <span className="text-[9px] uppercase tracking-[0.2em] text-slate-400 mt-1">Assets</span>
        <button
          onClick={() => { setShowElements(!showElements); setShowStockPhotos(false); }}
          className={`p-3 rounded-xl transition-colors ${showElements ? 'bg-teal-100 dark:bg-teal-900/50 text-teal-700' : 'hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-400'}`}
          title="Elements & Stickers"
        >
          <Sparkles className="w-5 h-5" />
        </button>
        <button
          onClick={() => { setShowStockPhotos(!showStockPhotos); setShowElements(false); }}
          className={`p-3 rounded-xl transition-colors ${showStockPhotos ? 'bg-teal-100 dark:bg-teal-900/50 text-teal-700' : 'hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-400'}`}
          title="Stock Photos"
        >
          <Camera className="w-5 h-5" />
        </button>

        <div className="flex-1" />

        <button
          onClick={() => setZoom(z => Math.max(0.25, z - 0.25))}
          className="p-3 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-400 transition-colors"
          title="Zoom Out"
        >
          <ZoomOut className="w-5 h-5" />
        </button>
        <span className="text-xs text-slate-500">{Math.round(zoom * 100)}%</span>
        <button
          onClick={() => setZoom(z => Math.min(2, z + 0.25))}
          className="p-3 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-400 transition-colors"
          title="Zoom In"
        >
          <ZoomIn className="w-5 h-5" />
        </button>
      </div>

      {/* Elements Panel */}
      {showElements && (
        <div className="w-72 bg-white/95 dark:bg-slate-900 border-r border-slate-200/70 dark:border-slate-800 flex flex-col backdrop-blur">
          <div className="p-3 border-b border-slate-200 dark:border-slate-700">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-teal-600" />
                Elements
              </h3>
              <button
                onClick={() => setShowElements(false)}
                className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search elements..."
                value={elementSearch}
                onChange={(e) => setElementSearch(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 text-sm bg-slate-100 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400"
              />
            </div>
          </div>

          {/* Category Tabs */}
          <div className="flex gap-1 p-2 overflow-x-auto border-b border-slate-200 dark:border-slate-700">
            {Object.entries(ELEMENT_CATEGORIES).map(([key, cat]) => {
              const CatIcon = cat.icon;
              return (
                <button
                  key={key}
                  onClick={() => setElementCategory(key)}
                  className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium whitespace-nowrap transition-colors ${
                    elementCategory === key
                      ? 'bg-teal-100 dark:bg-teal-900/50 text-teal-700'
                      : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700'
                  }`}
                >
                  <CatIcon className="w-3 h-3" />
                  {cat.name}
                </button>
              );
            })}
          </div>

          {/* Elements Grid */}
          <div className="flex-1 overflow-y-auto p-3">
            <div className="grid grid-cols-3 gap-2">
              {(ELEMENT_CATEGORIES[elementCategory]?.items || [])
                .filter(item => !elementSearch || item.name.toLowerCase().includes(elementSearch.toLowerCase()))
                .map((item) => (
                  <button
                    key={item.id}
                    onClick={() => addElement(item)}
                    className="group relative aspect-square bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 border border-slate-200 dark:border-slate-600 rounded-lg p-2 transition-all hover:scale-105"
                    title={item.name}
                  >
                    {item.type === 'emoji' ? (
                      <span className="text-2xl flex items-center justify-center w-full h-full">{item.emoji}</span>
                    ) : item.type === 'badge' ? (
                      <div
                        className="w-full h-full flex items-center justify-center rounded text-[8px] font-bold px-1"
                        style={{ backgroundColor: item.bgColor, color: item.textColor }}
                      >
                        {item.text}
                      </div>
                    ) : item.icon ? (
                      <div className="w-full h-full flex items-center justify-center">
                        <item.icon className="w-6 h-6" style={{ color: item.color }} />
                      </div>
                    ) : null}
                    <div className="absolute inset-x-0 bottom-0 bg-black/60 p-0.5 rounded-b-lg opacity-0 group-hover:opacity-100 transition-opacity">
                      <p className="text-[8px] text-white text-center truncate">{item.name}</p>
                    </div>
                  </button>
                ))}
            </div>

            {/* Pro Tip */}
            <div className="mt-4 p-3 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-lg">
              <p className="text-xs text-teal-700 dark:text-teal-300">
                <strong>Tip:</strong> Click any element to add it to your design. Drag to reposition.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Stock Photos Panel */}
      {showStockPhotos && (
        <div className="w-80 bg-white/95 dark:bg-slate-900 border-r border-slate-200/70 dark:border-slate-800 flex flex-col backdrop-blur">
          <div className="p-3 border-b border-slate-200 dark:border-slate-700">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                <Camera className="w-4 h-4 text-teal-600" />
                Stock Photos
              </h3>
              <button
                onClick={() => setShowStockPhotos(false)}
                className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search photos..."
                value={stockSearch}
                onChange={(e) => setStockSearch(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 text-sm bg-slate-100 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400"
              />
            </div>
          </div>

          {/* Category Tabs */}
          <div className="flex gap-1 p-2 overflow-x-auto border-b border-slate-200 dark:border-slate-700">
            <button
              onClick={() => setStockCategory('all')}
              className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium whitespace-nowrap transition-colors ${
                stockCategory === 'all'
                  ? 'bg-teal-100 dark:bg-teal-900/50 text-teal-700'
                  : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700'
              }`}
            >
              <Image className="w-3 h-3" />
              All
            </button>
            <button
              onClick={() => setStockCategory('technology')}
              className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium whitespace-nowrap transition-colors ${
                stockCategory === 'technology'
                  ? 'bg-teal-100 dark:bg-teal-900/50 text-teal-700'
                  : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700'
              }`}
            >
              <Monitor className="w-3 h-3" />
              Tech
            </button>
            <button
              onClick={() => setStockCategory('approval')}
              className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium whitespace-nowrap transition-colors ${
                stockCategory === 'approval'
                  ? 'bg-teal-100 dark:bg-teal-900/50 text-teal-700'
                  : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700'
              }`}
            >
              <Check className="w-3 h-3" />
              Approved
            </button>
            <button
              onClick={() => setStockCategory('professional')}
              className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium whitespace-nowrap transition-colors ${
                stockCategory === 'professional'
                  ? 'bg-teal-100 dark:bg-teal-900/50 text-teal-700'
                  : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700'
              }`}
            >
              <Briefcase className="w-3 h-3" />
              Pro
            </button>
            <button
              onClick={() => setStockCategory('lifestyle')}
              className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium whitespace-nowrap transition-colors ${
                stockCategory === 'lifestyle'
                  ? 'bg-teal-100 dark:bg-teal-900/50 text-teal-700'
                  : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700'
              }`}
            >
              <Users className="w-3 h-3" />
              Lifestyle
            </button>
          </div>

          {/* Photos Grid */}
          <div className="flex-1 overflow-y-auto p-3">
            <div className="grid grid-cols-2 gap-2">
              {(stockCategory === 'all' ? ALL_STOCK_PHOTOS : STOCK_PHOTOS[stockCategory] || [])
                .filter(photo => !stockSearch || photo.name.toLowerCase().includes(stockSearch.toLowerCase()))
                .map((photo) => (
                  <button
                    key={photo.id}
                    onClick={() => addStockPhoto(photo)}
                    className="group relative aspect-[4/3] bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 border border-slate-200 dark:border-slate-600 rounded-lg overflow-hidden transition-all hover:scale-[1.02] hover:shadow-lg"
                    title={photo.name}
                  >
                    <img
                      src={photo.src}
                      alt={photo.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <p className="text-[10px] text-white text-center truncate font-medium">{photo.name}</p>
                    </div>
                  </button>
                ))}
            </div>

            {/* Info tip */}
            <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <p className="text-xs text-amber-700 dark:text-amber-300">
                <strong>Tip:</strong> Click any photo to add it to your design. Drag to reposition, use corner handles to resize.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Canvas Area */}
      <div className="flex-1 bg-[#efece6] dark:bg-slate-900 overflow-auto flex items-center justify-center p-10 relative">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(circle at 20% 20%, rgba(15, 118, 110, 0.12), transparent 45%), radial-gradient(circle at 80% 30%, rgba(245, 158, 11, 0.12), transparent 40%)'
          }}
        />
        <div className="relative">
          <div className="rounded-2xl border border-slate-200/80 dark:border-slate-700 bg-white shadow-[0_20px_60px_rgba(15,23,42,0.18)] p-3">
            <canvas
              ref={canvasRef}
              onClick={handleCanvasClick}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onDoubleClick={handleDoubleClick}
              className="cursor-crosshair bg-white rounded-xl"
              style={{
                width: canvasWidth * zoom,
                height: canvasHeight * zoom
              }}
            />
          </div>
          {/* Loading Overlay */}
          {isLoadingImage && (
            <div className="absolute inset-0 bg-slate-900/40 flex items-center justify-center rounded-2xl">
              <div className="bg-white dark:bg-slate-800 px-6 py-4 rounded-xl shadow-xl flex items-center gap-3">
                <Loader2 className="w-6 h-6 text-teal-600 animate-spin" />
                <span className="text-slate-700 dark:text-slate-200 font-medium">Loading image...</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Right Panel - Properties */}
      <div className="w-64 bg-white/95 dark:bg-slate-900 border-l border-slate-200/70 dark:border-slate-800 p-4 overflow-y-auto backdrop-blur">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Properties</h3>
          <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Inspector</span>
        </div>

        {/* Background Color */}
        <div className="mb-4">
          <label className="text-xs text-slate-500 dark:text-slate-400 mb-2 block">Background</label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={backgroundColor}
              onChange={(e) => setBackgroundColor(e.target.value)}
              className="w-9 h-9 rounded-lg cursor-pointer border border-slate-200"
            />
            <input
              type="text"
              value={backgroundColor}
              onChange={(e) => setBackgroundColor(e.target.value)}
              className="flex-1 px-2.5 py-1.5 text-sm bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg"
            />
          </div>
        </div>

        {/* Selected Element Properties */}
        {selectedElement !== null && elements[selectedElement] && (
          <>
            <div className="border-t border-slate-200 dark:border-slate-700 pt-4 mt-4">
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs font-medium text-slate-700 dark:text-slate-300 uppercase">
                  {elements[selectedElement].type}
                </span>
                <button
                  onClick={deleteSelected}
                  className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500 transition-colors"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              {/* Color picker for selected element */}
              <div className="mb-4">
                <label className="text-xs text-slate-500 dark:text-slate-400 mb-2 block">Color</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={elements[selectedElement].color || '#000000'}
                    onChange={(e) => updateSelectedColor(e.target.value)}
                    className="w-9 h-9 rounded-lg cursor-pointer border border-slate-200"
                  />
                  <input
                    type="text"
                    value={elements[selectedElement].color || '#000000'}
                    onChange={(e) => updateSelectedColor(e.target.value)}
                    className="flex-1 px-2.5 py-1.5 text-sm bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg"
                  />
                </div>
              </div>

              {/* Text-specific options */}
              {elements[selectedElement].type === 'text' && (
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-slate-500 dark:text-slate-400 mb-2 block">Font Size</label>
                    <input
                      type="number"
                      value={elements[selectedElement].fontSize || 32}
                      onChange={(e) => setElements(prev => prev.map((el, i) =>
                        i === selectedElement ? { ...el, fontSize: parseInt(e.target.value) || 32 } : el
                      ))}
                      className="w-full px-2.5 py-1.5 text-sm bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg"
                    />
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => setElements(prev => prev.map((el, i) =>
                        i === selectedElement ? { ...el, fontWeight: el.fontWeight === 'bold' ? 'normal' : 'bold' } : el
                      ))}
                      className={`p-2 rounded ${elements[selectedElement].fontWeight === 'bold' ? 'bg-teal-100 dark:bg-teal-900/50 text-teal-700' : 'hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-400'}`}
                    >
                      <Bold className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setElements(prev => prev.map((el, i) =>
                        i === selectedElement ? { ...el, fontStyle: el.fontStyle === 'italic' ? 'normal' : 'italic' } : el
                      ))}
                      className={`p-2 rounded ${elements[selectedElement].fontStyle === 'italic' ? 'bg-teal-100 dark:bg-teal-900/50 text-teal-700' : 'hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-400'}`}
                    >
                      <Italic className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* Size for shapes and images */}
              {elements[selectedElement].type !== 'text' && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">Width</label>
                    <input
                      type="number"
                      value={Math.round(elements[selectedElement].width)}
                      onChange={(e) => setElements(prev => prev.map((el, i) =>
                        i === selectedElement ? { ...el, width: parseInt(e.target.value) || 100 } : el
                      ))}
                      className="w-full px-2.5 py-1.5 text-sm bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">Height</label>
                    <input
                      type="number"
                      value={Math.round(elements[selectedElement].height)}
                      onChange={(e) => setElements(prev => prev.map((el, i) =>
                        i === selectedElement ? { ...el, height: parseInt(e.target.value) || 100 } : el
                      ))}
                      className="w-full px-2.5 py-1.5 text-sm bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg"
                    />
                  </div>
                </div>
              )}

              {/* Lock aspect ratio hint for images */}
              {elements[selectedElement].type === 'image' && (
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                  Tip: Drag corner handles to resize. Use Shift for proportional.
                </p>
              )}
            </div>
          </>
        )}

        {/* Quick Colors */}
        <div className="border-t border-slate-200 dark:border-slate-700 pt-4 mt-4">
          <label className="text-xs text-slate-500 dark:text-slate-400 mb-2 block">
            Quick Colors {selectedElement !== null ? '(Element)' : '(Background)'}
          </label>
          <div className="grid grid-cols-6 gap-1">
            {['#000000', '#ffffff', '#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#8b5cf6', '#ec4899', '#6b7280', '#0ea5e9', '#14b8a6'].map(color => (
              <button
                key={color}
                onClick={() => {
                  if (selectedElement !== null) {
                    updateSelectedColor(color);
                  } else {
                    setBackgroundColor(color);
                  }
                }}
                className="w-8 h-8 rounded-lg border border-slate-300 dark:border-slate-600 hover:scale-110 transition-transform"
                style={{ backgroundColor: color }}
              />
            ))}
          </div>
        </div>

        {/* Export Button */}
        <div className="border-t border-slate-200 dark:border-slate-700 pt-4 mt-4">
          <button
            onClick={handleExport}
            className="w-full py-2.5 bg-teal-600 hover:bg-teal-500 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
          >
            <Download className="w-4 h-4" />
            Export PNG
          </button>
        </div>
      </div>
    </div>
  );
});

const PhotoEditor = ({ onBack, onSave, project, featureFlags = {} }) => {
  const [projectName, setProjectName] = useState(project?.name || 'Untitled Design');
  const [isEditingName, setIsEditingName] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const editorRef = useRef(null);
  const statusLabel = isSaving ? 'Saving...' : hasChanges ? 'Unsaved changes' : 'All changes saved';
  const statusTone = isSaving
    ? 'bg-slate-100 text-slate-600'
    : hasChanges
      ? 'bg-amber-100 text-amber-700'
      : 'bg-teal-100 text-teal-700';

  // Template from project or default
  const template = project ? {
    label: TEMPLATE_PRESETS[project.templateId]?.label || 'Custom',
    width: project.width,
    height: project.height,
    thumbnail: TEMPLATE_PRESETS[project.templateId]?.thumbnail
  } : TEMPLATE_PRESETS.instagram_post;

  // Handle save
  const handleSave = useCallback(async () => {
    if (!onSave || !project || !editorRef.current) return;

    setIsSaving(true);
    try {
      const { elements, backgroundColor, generateThumbnail } = editorRef.current;
      const thumbnail = await generateThumbnail();

      onSave({
        ...project,
        name: projectName,
        elements: elements.map(el => {
          // Don't save the actual Image objects, save references
          if (el.type === 'image' && el.image) {
            return { ...el, image: null, imageSrc: el.image.src };
          }
          return el;
        }),
        backgroundColor,
        thumbnail
      });

      setHasChanges(false);
    } catch (error) {
      console.error('Failed to save project:', error);
      alert('Failed to save project. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, [onSave, project, projectName]);

  // Auto-save on unmount if there are changes
  useEffect(() => {
    return () => {
      if (hasChanges && onSave && project) {
        // Note: This won't work perfectly due to async nature, but it's a safety net
      }
    };
  }, [hasChanges, onSave, project]);

  return (
    <div className="flex flex-col h-full bg-white dark:bg-slate-950">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-slate-200/80 dark:border-slate-800 bg-white/90 dark:bg-slate-900/80 backdrop-blur">
        <div className="flex items-center gap-4">
          <button
            onClick={() => {
              if (hasChanges) {
                if (window.confirm('You have unsaved changes. Save before leaving?')) {
                  handleSave().then(() => onBack());
                  return;
                }
              }
              onBack();
            }}
            className="p-2 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            {isEditingName ? (
              <input
                type="text"
                value={projectName}
                onChange={(e) => {
                  setProjectName(e.target.value);
                  setHasChanges(true);
                }}
                onBlur={() => setIsEditingName(false)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') setIsEditingName(false);
                }}
                autoFocus
                className="text-lg font-semibold text-slate-900 dark:text-white bg-transparent border-b-2 border-teal-500 focus:outline-none"
              />
            ) : (
              <h1
                onClick={() => setIsEditingName(true)}
                className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2 cursor-pointer hover:text-teal-600 transition-colors"
              >
                <Image className="w-5 h-5 text-teal-600" />
                {projectName}
                {hasChanges && <span className="text-xs text-orange-500">(unsaved)</span>}
              </h1>
            )}
            <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-slate-500 dark:text-slate-400">
              <span className="px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
                {template.label}
              </span>
              <span className="px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
                {template.width} x {template.height} px
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusTone}`}>
            {statusLabel}
          </span>
          {/* Save Button */}
          {onSave && (
            <button
              onClick={handleSave}
              disabled={isSaving}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
                hasChanges
                  ? 'bg-teal-600 hover:bg-teal-500 text-white'
                  : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400'
              }`}
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Editor */}
      <div className="flex-1 min-h-0">
        <CanvasEditor
          ref={editorRef}
          template={template}
          initialElements={project?.elements}
          initialBackgroundColor={project?.backgroundColor}
          onChanges={() => setHasChanges(true)}
        />
      </div>
    </div>
  );
};

export default PhotoEditor;
